import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

/**
 * Single-line truncating text that reveals the full content in a floating
 * tooltip on hover — only when the text is actually overflowing. The tooltip
 * is portaled to the body so it isn't clipped by parent ``overflow-hidden``
 * containers (e.g. the list card). A ``cursor-help`` pointer shows the hint
 * is interactive when truncation is in effect, and the native ``title``
 * attribute is set as an accessibility / keyboard-only fallback.
 */
export function TruncatedText({
  text,
  className = '',
  tooltipClassName = '',
}: {
  text: string;
  className?: string;
  tooltipClassName?: string;
}) {
  const elRef = useRef<HTMLDivElement>(null);
  const [isTruncated, setIsTruncated] = useState(false);
  const [hover, setHover] = useState(false);
  const [pos, setPos] = useState<{ left: number; top: number } | null>(null);

  useEffect(() => {
    const el = elRef.current;
    if (!el) return;
    const measure = () => setIsTruncated(el.scrollWidth - 1 > el.clientWidth);
    measure();
    const obs = new ResizeObserver(measure);
    obs.observe(el);
    return () => obs.disconnect();
  }, [text]);

  // Close on scroll or resize so the tooltip doesn't drift from its anchor.
  useEffect(() => {
    if (!hover) return;
    const onScrollOrResize = () => setHover(false);
    window.addEventListener('scroll', onScrollOrResize, true);
    window.addEventListener('resize', onScrollOrResize);
    return () => {
      window.removeEventListener('scroll', onScrollOrResize, true);
      window.removeEventListener('resize', onScrollOrResize);
    };
  }, [hover]);

  function handleEnter() {
    if (!isTruncated) return;
    const rect = elRef.current?.getBoundingClientRect();
    if (!rect) return;
    setPos({ left: rect.left, top: rect.bottom + 6 });
    setHover(true);
  }

  return (
    <>
      <div
        ref={elRef}
        className={
          className +
          ' truncate' +
          (isTruncated ? ' cursor-help hover:text-slate-700' : '')
        }
        onMouseEnter={handleEnter}
        onMouseLeave={() => setHover(false)}
      >
        {text}
      </div>
      {hover &&
        pos &&
        createPortal(
          <div
            role="tooltip"
            className={
              'pointer-events-none fixed z-50 max-w-[min(32rem,92vw)] bg-slate-900 text-white text-xs rounded-md px-2.5 py-1.5 shadow-lg leading-snug whitespace-normal break-words ' +
              tooltipClassName
            }
            style={{ left: pos.left, top: pos.top }}
          >
            {text}
          </div>,
          document.body,
        )}
    </>
  );
}
